import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ModalAction from '@/components/ModalAction';
import LoaderIcon from '@/images/icons/Loader';
import { fetchAPI } from '@/api';
import { Input } from '@/components/Form/Input';

const CouponModal = ({ modalOpen, setModalOpen, setErrorModal }: any) => {
    const { auth, project } = useOutletContext() as any;

    const [couponCode, setCouponCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [localError, setLocalError] = useState('');

    const onClearModal = () => {
        setModalOpen(false);
        setTimeout(() => {
            setCouponCode('');
            setIsLoading(false);
            setErrorModal(null);
            setIsSuccess(false);
            setLocalError('');
        }, 400);
    };

    const onApplyCoupon = async () => {
        setIsLoading(true);
        setErrorModal(null);
        setLocalError('');

        if (couponCode.trim().length === 0) {
            setLocalError('Please enter a coupon code');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetchAPI("/billing/apply-coupon", auth?.user?.token, {
                projectId: project.projectId,
                couponCode: couponCode.trim(),
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            if (data?.message) {
                setIsSuccess(true);
            }
        } catch (error: any) {
            console.error('Error applying coupon:', error);
            let errorMessage = 'Failed to apply coupon. Please check the code and try again.';
            if (error.response?.message) {
                errorMessage = error.response.message;
            }
            setErrorModal(errorMessage);
            setModalOpen(false);
            setIsSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalAction
            id="coupon-modal"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onModalClose={() => onClearModal()}
            size={undefined}
        >
            {!isSuccess ? (
                <>
                    <h2 className="h2 mb-4 pr-16 md:pr-14 leading-[0.9]">
                        Enter Coupon
                    </h2>
                    <div>
                        <div className="mb-4">Enter your coupon code to apply a discount to your subscription.</div>
                        
                        <Input 
                            className="h-11"
                            type="text"
                            name="couponCode"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            required
                        >
                            Coupon Code
                        </Input>
                        
                        <button 
                            className="btn-primary w-full mt-8" 
                            onClick={() => onApplyCoupon()}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center py-1 pointer-events-none">
                                    <LoaderIcon className="animate-rotation w-6 h-6 text-brand-green" />
                                </div>
                            ) : (
                                <span className="pointer-events-none">Apply</span>
                            )}
                        </button>
                    </div>

                    <button className="btn-primary-inverted w-full mt-4" onClick={() => onClearModal()}>
                        <span className="pointer-events-none">Cancel</span>
                    </button>

                    {localError && (
                        <div className="text-sm text-red-orange mt-2 font-medium">{localError}</div>
                    )}
                </>
            ) : (
                <>
                    <h2 className="h2 mb-4 pr-16 md:pr-14 leading-[0.9]">
                        Coupon Applied!
                    </h2>
                    <div className="mb-4">Your coupon has been successfully applied.</div>
                    <button className="btn-primary w-full" onClick={() => onClearModal()}>
                        <span className="pointer-events-none">Ok, thanks</span>
                    </button>
                </>
            )}
        </ModalAction>
    );
};

export default CouponModal;
